import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, Check, CreditCard, Shield, Clock, 
  ArrowRight, CheckCircle, PartyPopper, Star, Users, MapPin, AlertCircle,
  AlertTriangle, ChevronLeft, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Plan limits mapping
const PLAN_LIMITS = {
  'starter_monthly': { locations: 3, therapists: 10 },
  'starter_annual': { locations: 3, therapists: 10 },
  'professional_monthly': { locations: 10, therapists: 50 },
  'professional_annual': { locations: 10, therapists: 50 },
  'enterprise_monthly': { locations: -1, therapists: -1 }, // -1 = unlimited
  'enterprise_annual': { locations: -1, therapists: -1 }
};

// Check if a plan fits the requirements
const checkPlanFitsRequirements = (planId, locations, staff, plans) => {
  const limits = PLAN_LIMITS[planId];
  if (!limits) return false;
  
  // -1 means unlimited
  const locationsOk = limits.locations === -1 || locations <= limits.locations;
  const staffOk = limits.therapists === -1 || staff <= limits.therapists;
  
  return locationsOk && staffOk;
};

// Get recommended plan based on locations and staff
const getRecommendedPlan = (locations, staff, plans) => {
  // Priority order: starter -> professional -> enterprise
  const planPriority = ['starter_monthly', 'professional_monthly', 'enterprise_monthly'];
  
  for (const planId of planPriority) {
    if (checkPlanFitsRequirements(planId, locations, staff, plans)) {
      return plans.find(p => p.id === planId);
    }
  }
  
  // Default to enterprise if nothing fits
  return plans.find(p => p.id === 'enterprise_monthly');
};

export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('company');
  const planId = searchParams.get('plan');
  
  // Get company info from localStorage if available
  const pendingReg = JSON.parse(localStorage.getItem('pending_registration') || '{}');
  
  // Use URL params first, fallback to localStorage
  const numLocations = parseInt(searchParams.get('locations')) || pendingReg.num_spa_locations || 1;
  const numStaff = parseInt(searchParams.get('staff')) || pendingReg.num_staff_to_manage || 1;
  
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [company, setCompany] = useState(pendingReg);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(planId || pendingReg.selected_plan || 'starter_monthly');
  const [recommendedPlan, setRecommendedPlan] = useState(null);
  const [trialDays, setTrialDays] = useState(7);
  const [autoRenewal, setAutoRenewal] = useState(true); // Default to auto-renewal
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);
  
  // Platform status state
  const [platformStatus, setPlatformStatus] = useState({
    platform_mode: 'test',
    razorpay_configured: false,
    razorpay_key_id: null
  });
  const [loadingStatus, setLoadingStatus] = useState(true);
  
  // Dynamically find the best plan when plans are loaded
  useEffect(() => {
    if (plans.length > 0) {
      const recommended = getRecommendedPlan(numLocations, numStaff, plans);
      setRecommendedPlan(recommended);
      // Auto-select recommended plan if no plan was pre-selected or current selection doesn't fit requirements
      if (!selectedPlanId || !checkPlanFitsRequirements(selectedPlanId, numLocations, numStaff, plans)) {
        setSelectedPlanId(recommended?.id);
      }
    }
  }, [plans, numLocations, numStaff]);
  
  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  useEffect(() => {
    fetchPlans();
    fetchPlatformStatus();
    if (companyId) {
      fetchCompanyInfo();
    }
  }, [companyId]);

  const fetchPlatformStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/platform-status`);
      if (res.ok) {
        const data = await res.json();
        setPlatformStatus(data);
        if (data.trial_days) {
          setTrialDays(data.trial_days);
        }
      }
    } catch (error) {
      console.error('Failed to fetch platform status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/plans`);
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans || []);
        // Get trial days from API
        if (data.trial_days) {
          setTrialDays(data.trial_days);
        }
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchCompanyInfo = async () => {
    // Already loaded from localStorage in useState
    // Try to get more info from API if companyId exists
    if (companyId && !company?.company_id) {
      try {
        const res = await fetch(`${API_URL}/api/tenant/companies/${companyId}`);
        if (res.ok) {
          const data = await res.json();
          setCompany(prev => ({...prev, ...data}));
        }
      } catch (error) {
        console.log('Could not fetch company info');
      }
    }
  };

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Razorpay'));
      document.body.appendChild(script);
    });
  };

  const handleStartTrial = async () => {
    // If no companyId, redirect to login with success message
    if (!companyId) {
      toast.success('Account created! Please login to continue.');
      navigate('/login');
      return;
    }
    
    if (!selectedPlanId) {
      toast.error('Please select a plan');
      return;
    }

    // Show payment choice modal
    setShowPaymentChoice(true);
  };

  const proceedWithPayment = async () => {
    setShowPaymentChoice(false);
    setLoading(true);
    
    try {
      // Check if Razorpay is configured
      if (!platformStatus.razorpay_configured || !platformStatus.razorpay_key_id) {
        // No Razorpay, start trial without card
        await startTrialWithoutCard();
        return;
      }

      if (autoRenewal) {
        // Auto-renewal: Create subscription with Razorpay (card saved)
        await startTrialWithAutoRenewal();
      } else {
        // One-time: Start trial without card capture
        await startTrialWithoutCard();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to start trial');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const startTrialWithAutoRenewal = async () => {
    // Create subscription with Razorpay for card capture
    const subRes = await fetch(`${API_URL}/api/tenant/create-trial-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_id: companyId,
        plan: selectedPlanId
      })
    });
    
    const subData = await subRes.json();
    
    if (!subRes.ok) {
      throw new Error(subData.detail || 'Failed to create subscription');
    }
    
    // Load Razorpay and open checkout
    await loadRazorpayScript();
    
    const options = {
      key: platformStatus.razorpay_key_id,
      subscription_id: subData.subscription_id,
      name: 'WellNest Technologies',
      description: `${subData.plan_name} Plan - ${trialDays}-Day Free Trial`,
      image: 'https://wellnesttechnologies.com/logo.png',
      handler: async function (response) {
        // Verify card capture
        try {
          const verifyRes = await fetch(`${API_URL}/api/tenant/confirm-trial-card?subscription_id=${response.razorpay_subscription_id}&razorpay_payment_id=${response.razorpay_payment_id}&razorpay_signature=${response.razorpay_signature}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (verifyRes.ok) {
            setPaymentComplete(true);
            localStorage.removeItem('pending_registration');
            toast.success('Your trial has started! Card saved for auto-renewal.');
          } else {
            const errorData = await verifyRes.json();
            toast.error(errorData.detail || 'Card verification failed');
          }
        } catch (err) {
          toast.error('Failed to verify card. Please contact support.');
        }
        setLoading(false);
      },
      modal: {
        ondismiss: function() {
          setLoading(false);
          toast.info('Payment cancelled. You can try again anytime.');
        }
      },
      prefill: {
        name: company?.admin_name || '',
        email: company?.admin_email || '',
        contact: company?.admin_phone || ''
      },
      theme: {
        color: '#0d9488'
      }
    };
    
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const startTrialWithoutCard = async () => {
    // Start trial without card capture (one-time payment later)
    const res = await fetch(`${API_URL}/api/tenant/start-trial-without-card`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_id: companyId,
        plan: selectedPlanId,
        auto_renewal: false
      })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Failed to start trial');
    }
    
    setPaymentComplete(true);
    localStorage.removeItem('pending_registration');
    toast.success(`Your ${trialDays}-day trial has started! You can add payment details later.`);
    setLoading(false);
  };

  const handleGoToApp = () => {
    navigate('/login');
  };

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-slate-800/50 border-slate-700">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
              <PartyPopper className="w-10 h-10 text-emerald-400" />
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-4">
              Welcome to WellNest Technologies!
            </h1>
            
            <p className="text-slate-300 mb-6">
              Your {trialDays}-day free trial has started. You have full access to all features.
            </p>
            
            <div className="bg-slate-700/30 rounded-xl p-4 mb-6 text-left">
              <h3 className="text-white font-semibold mb-3">Next Steps:</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  Login with your email and password
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  Add your properties and rooms
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  Invite your staff
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  Start taking bookings!
                </li>
              </ul>
            </div>
            
            {company && company.subdomain && (
              <div className="text-sm text-slate-400 mb-6">
                Your app URL: <span className="text-emerald-400">{company.subdomain}.wellnesttechnologies.com</span>
              </div>
            )}
            
            <Button onClick={handleGoToApp} size="lg"
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600">
              Go to Login <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">WellNest Technologies</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        {/* Recommendation Banner */}
        {recommendedPlan && (numLocations > 1 || numStaff > 1) && (
          <div className="mb-8 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <Star className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-emerald-400 font-semibold mb-1">Package Recommendation</h3>
                <p className="text-slate-300 text-sm">
                  Based on your requirements ({numLocations} location{numLocations > 1 ? 's' : ''} and {numStaff} staff member{numStaff > 1 ? 's' : ''}), 
                  we recommend the <span className="text-white font-semibold">{recommendedPlan.name}</span> plan.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Plan Selection */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4">Choose Your Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.filter(p => p.billing_cycle === 'monthly').map((plan) => {
              const limits = PLAN_LIMITS[plan.id] || { locations: 0, therapists: 0 };
              const fitsRequirements = checkPlanFitsRequirements(plan.id, numLocations, numStaff, plans);
              const isRecommended = recommendedPlan?.id === plan.id;
              const isSelected = selectedPlanId === plan.id;
              
              return (
                <div 
                  key={plan.id}
                  onClick={() => {
                    if (fitsRequirements) {
                      setSelectedPlanId(plan.id);
                    } else {
                      toast.error(`This plan doesn't support your requirements (${numLocations} locations, ${numStaff} staff)`);
                    }
                  }}
                  className={`relative p-4 rounded-xl border-2 transition-all ${
                    !fitsRequirements 
                      ? 'border-slate-700 bg-slate-800/30 opacity-60 cursor-not-allowed'
                      : isSelected
                        ? 'border-emerald-500 bg-emerald-500/10 cursor-pointer'
                        : 'border-slate-600 hover:border-slate-500 cursor-pointer'
                  }`}
                >
                  {isRecommended && fitsRequirements && (
                    <Badge className="absolute -top-2 -right-2 bg-emerald-500 text-white">
                      Recommended
                    </Badge>
                  )}
                  
                  {!fitsRequirements && (
                    <Badge className="absolute -top-2 -right-2 bg-red-500/80 text-white">
                      Insufficient
                    </Badge>
                  )}
                  
                  <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                  <p className="text-2xl font-bold text-white mt-2">
                    {formatCurrency(plan.amount)}
                    <span className="text-sm text-slate-400 font-normal">/mo</span>
                  </p>
                  
                  {/* Plan Limits */}
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {limits.locations === -1 ? 'Unlimited' : `Up to ${limits.locations}`} locations
                      {numLocations > limits.locations && limits.locations !== -1 && (
                        <AlertCircle className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Users className="w-4 h-4 text-slate-400" />
                      {limits.therapists === -1 ? 'Unlimited' : `Up to ${limits.therapists}`} staff
                      {numStaff > limits.therapists && limits.therapists !== -1 && (
                        <AlertCircle className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  </div>
                  
                  <ul className="mt-4 space-y-2">
                    {plan.features.slice(0, 3).map((feature, i) => (
                      <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card className="bg-slate-800/50 border-slate-700 h-fit">
            <CardHeader>
              <CardTitle className="text-white">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedPlan && (
                <>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-white font-semibold">{selectedPlan.name} Plan</h3>
                      <p className="text-sm text-slate-400">
                        {selectedPlan.billing_cycle === 'monthly' ? 'Billed monthly' : 'Billed annually'}
                      </p>
                    </div>
                    <span className="text-white font-semibold">
                      {formatCurrency(selectedPlan.amount)}
                    </span>
                  </div>
                  
                  <div className="border-t border-slate-700 pt-4">
                    <h4 className="text-slate-300 text-sm mb-3">Includes:</h4>
                    <ul className="space-y-2">
                      {selectedPlan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                          <Check className="w-4 h-4 text-emerald-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
              
              <div className="border-t border-slate-700 pt-4 space-y-2">
                <div className="flex justify-between text-slate-300">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedPlan?.amount || 0)}</span>
                </div>
                <div className="flex justify-between text-emerald-400">
                  <span>{trialDays}-day Trial Discount</span>
                  <span>-{formatCurrency(selectedPlan?.amount || 0)}</span>
                </div>
                <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-slate-700">
                  <span>Due Today</span>
                  <span className="text-emerald-400">₹0</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment / Trial Start */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                Start Your Free Trial
              </CardTitle>
              <CardDescription className="text-slate-400">
                {platformStatus.platform_mode === 'live' 
                  ? platformStatus.razorpay_configured 
                    ? 'Add your card to start trial. You won\'t be charged today.'
                    : 'Complete registration to start your free trial'
                  : 'Platform is currently in testing mode'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Test Mode Info Banner */}
              {platformStatus.platform_mode === 'test' && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Test Mode Active
                  </h3>
                  <p className="text-slate-300 text-sm">
                    You are using test mode. Use test card <code className="bg-slate-700 px-1 rounded">4111 1111 1111 1111</code> with any expiry and CVV.
                  </p>
                </div>
              )}
              
              {/* Trial Info - Show for both modes */}
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                <h3 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {trialDays}-Day Free Trial
                </h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Full access to all features
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    {platformStatus.razorpay_configured 
                      ? 'Card saved for seamless auto-renewal' 
                      : 'No card required to start'}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Cancel anytime before trial ends - no charges
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    We'll remind you {platformStatus.reminder_days_before || 3} days before billing
                  </li>
                </ul>
                {platformStatus.razorpay_configured && (
                  <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                    <CreditCard className="w-3 h-3" />
                    You'll be charged {formatCurrency(selectedPlan?.amount || 0)} after {trialDays} days
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={handleStartTrial} 
                  disabled={loading || !selectedPlanId} 
                  size="lg"
                  className="w-full text-lg bg-gradient-to-r from-emerald-500 to-teal-600"
                >
                  {loading 
                    ? 'Processing...' 
                    : platformStatus.razorpay_configured
                      ? `Add Card & Start ${trialDays}-Day Trial`
                      : `Start ${trialDays}-Day Free Trial`}
                </Button>
                
                {platformStatus.platform_mode === 'test' && (
                  <p className="text-center text-xs text-blue-400 flex items-center justify-center gap-1">
                    <Shield className="w-3 h-3" />
                    Test Mode: Use card 4111 1111 1111 1111
                  </p>
                )}
                
                {platformStatus.razorpay_configured && (
                  <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1">
                    <Shield className="w-3 h-3" />
                    Secure payment powered by Razorpay. No charge today.
                  </p>
                )}
                
                <p className="text-center text-xs text-slate-500">
                  By starting your trial, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>

              {/* Back Button */}
              <div className="pt-4 border-t border-slate-700">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/onboarding')}
                  className="w-full border-slate-600 text-slate-400 hover:bg-slate-700"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back to Registration
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Payment Choice Modal */}
      {showPaymentChoice && selectedPlan && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-900 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-teal-400" />
                Choose How to Start Your Trial
              </CardTitle>
              <CardDescription className="text-slate-400">
                Your {trialDays}-day free trial starts now. Choose your payment preference:
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Auto-Renewal Option */}
              <div 
                onClick={() => setAutoRenewal(true)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  autoRenewal 
                    ? 'border-teal-500 bg-teal-500/10' 
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    autoRenewal ? 'border-teal-500 bg-teal-500' : 'border-slate-500'
                  }`}>
                    {autoRenewal && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold flex items-center gap-2">
                      Save Card for Auto-Renewal
                      <Badge className="bg-teal-500/20 text-teal-400 text-xs">Recommended</Badge>
                    </h4>
                    <p className="text-sm text-slate-400 mt-1">
                      Enter card details now. After trial, {formatCurrency(selectedPlan?.amount || 999)}/month will be charged automatically.
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-teal-400">
                      <Zap className="w-3 h-3" />
                      No interruption • Cancel anytime
                    </div>
                  </div>
                </div>
              </div>

              {/* Skip Card Option */}
              <div 
                onClick={() => setAutoRenewal(false)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  !autoRenewal 
                    ? 'border-amber-500 bg-amber-500/10' 
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    !autoRenewal ? 'border-amber-500 bg-amber-500' : 'border-slate-500'
                  }`}>
                    {!autoRenewal && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold">Skip Card • Pay Later</h4>
                    <p className="text-sm text-slate-400 mt-1">
                      Start your {trialDays}-day trial now. Add payment details from your dashboard before trial ends.
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-amber-400">
                      <AlertTriangle className="w-3 h-3" />
                      Manual payment required before trial ends
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300"
                  onClick={() => setShowPaymentChoice(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={proceedWithPayment}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : autoRenewal ? 'Save Card & Start Trial' : 'Start Trial'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
